/* eslint-disable react/prop-types, max-lines, max-len, id-length, max-lines-per-function */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import './BudgetChangeRequestModal.scss';
import darEditIcon from '../../../../Assets/images/svg/dar_edit.svg';
import darDeleteIcon from '../../../../Assets/images/svg/dar_delete.svg';
import { useSnackbar } from '../../../../Components/Snackbar/snackBarProvider';

const EMPTY_EFFORT = { resourceId: '', resourceSkill: '', effort: '', comment: '' };
const EMPTY_COST = { costCategoryId: '', costCategory: '', amount: '', comment: '' };
const VISIBLE_REQUEST_ROW_COUNT = 3;
const OPEN_REQUEST_STATUS = 'open';

const sanitizeDecimalInput = (value) => {
  const normalizedValue = String(value || '').replace(/[^\d.]/g, '');
  const [integerPart = '', ...fractionParts] = normalizedValue.split('.');
  const joinedFraction = fractionParts.join('');

  if (!normalizedValue.includes('.')) {
    return integerPart;
  }

  return `${integerPart}.${joinedFraction}`;
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '';

  const parsed = parseFloat(String(value).replace(/,/g, ''));
  if (Number.isNaN(parsed)) return String(value);

  return parsed.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatEffort = (value) => {
  if (value === null || value === undefined || value === '') return '';

  const parsed = parseFloat(String(value).replace(/,/g, ''));
  if (Number.isNaN(parsed)) return String(value);

  return parsed.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const calcEffortTotal = (rows = []) =>
  rows.reduce((sum, row) => sum + (parseFloat(row?.effort) || 0), 0);

const calcCostTotal = (rows = []) =>
  rows.reduce((sum, row) => sum + (parseFloat(String(row?.amount || '').replace(/,/g, '')) || 0), 0);

const normalizeEffortRows = (rows = []) => (
  Array.isArray(rows)
    ? rows.map((row) => ({
      resourceId: String(row?.resourceId ?? row?.resource_id ?? ''),
      resourceSkill: String(
        row?.resourceSkill
        ?? row?.resource_name
        ?? row?.label
        ?? '',
      ),
      effort: String(row?.effort ?? row?.efforts ?? ''),
      comment: String(row?.comment ?? row?.comments ?? ''),
    }))
    : []
);

const normalizeCostRows = (rows = []) => (
  Array.isArray(rows)
    ? rows.map((row) => ({
      costCategoryId: String(row?.costCategoryId ?? row?.cost_category_id ?? ''),
      costCategory: String(
        row?.costCategory
        ?? row?.cost_category_name
        ?? row?.label
        ?? '',
      ),
      amount: String(row?.amount ?? row?.cost ?? ''),
      comment: String(row?.comment ?? row?.comments ?? ''),
    }))
    : []
);

const getSelectedOptionIds = (rows = [], editingIndex, idKey) => (
  new Set(
    rows
      .filter((_, index) => index !== editingIndex)
      .map((row) => String(row?.[idKey] || ''))
      .filter(Boolean),
  )
);

const FormField = ({ label, required, showRequiredMark = true, hasError = false, children }) => (
  <div className="bcr-form-field">
    <label className={`bcr-form-label${hasError ? ' bcr-form-label--error' : ''}`}>
      {label}
      {required && showRequiredMark && <span className="bcr-required">*</span>}
    </label>
    {children}
  </div>
);

const SearchableSelect = ({
  value,
  selectedLabel,
  placeholder,
  options,
  disabledOptionIds,
  onSelect,
  onInputChange,
  onClear,
}) => {
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(selectedLabel || '');

  useEffect(() => {
    setQuery(selectedLabel || '');
  }, [selectedLabel, value]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleDocumentPointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentPointerDown);
    return () => document.removeEventListener('mousedown', handleDocumentPointerDown);
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = String(query || '').trim()
      .toLowerCase();

    if (!normalizedQuery) return options;

    return options.filter((option) => String(option.label || '')
      .toLowerCase()
      .includes(normalizedQuery));
  }, [options, query]);

  const shouldShowClear = Boolean(String(query || '').trim());

  return (
    <div className="bcr-searchable-select" ref={wrapperRef}>
      <input
        ref={inputRef}
        type="text"
        autoComplete="off"
        className={`bcr-input bcr-searchable-select__input${value ? ' has-value' : ''}`}
        placeholder={placeholder}
        value={query}
        onFocus={() => setIsOpen(true)}
        onClick={() => setIsOpen(true)}
        onChange={(event) => {
          const nextValue = event.target.value;
          setQuery(nextValue);
          setIsOpen(true);
          onInputChange(nextValue);
        }}
      />
      {shouldShowClear ? (
        <button
          type="button"
          className="bcr-searchable-select__clear"
          aria-label="Clear selection"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setQuery('');
            onInputChange('');
            onClear?.();
            setIsOpen(true);
            requestAnimationFrame(() => {
              inputRef.current?.focus();
            });
          }}
        />
      ) : (
        <span className={`bcr-searchable-select__caret${isOpen ? ' is-open' : ''}`} aria-hidden="true" />
      )}
      {isOpen && (
        <div className="bcr-searchable-select__menu">
          {filteredOptions.length ? (
            filteredOptions.map((option) => {
              const isDisabled = disabledOptionIds.has(String(option.value));

              return (
                <button
                  type="button"
                  key={option.value}
                  className={`bcr-searchable-select__option${isDisabled ? ' bcr-searchable-select__option--disabled' : ''}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    if (isDisabled) return;
                    onSelect(option);
                    setQuery(option.label);
                    setIsOpen(false);
                  }}
                >
                  {option.label}
                </button>
              );
            })
          ) : (
            <div className="bcr-searchable-select__empty">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

const RowActionButtons = ({ onEdit, onDelete, editLabel, deleteLabel }) => (
  <div className="bcr-action-cell__controls">
    <button
      type="button"
      className="bcr-icon-btn"
      onClick={onEdit}
      aria-label={editLabel}
      title="Edit"
    >
      <img src={darEditIcon} alt="" className="bcr-icon-btn__asset" />
    </button>
    <button
      type="button"
      className="bcr-icon-btn bcr-icon-btn--danger"
      onClick={onDelete}
      aria-label={deleteLabel}
      title="Delete"
    >
      <img src={darDeleteIcon} alt="" className="bcr-icon-btn__asset" />
    </button>
  </div>
);

const DeleteConfirmModal = ({
  title,
  message,
  onClose,
  onConfirm,
}) => (
  <div
    className="budget-change-request-delete-backdrop"
    onClick={(event) => event.target === event.currentTarget && onClose()}
  >
    <div className="budget-change-request-delete-modal">
      <div className="budget-change-request-delete-modal__header">{title}</div>
      <div className="budget-change-request-delete-modal__body">
        <div className="budget-change-request-delete-modal__warning" aria-hidden="true" />
        <div className="budget-change-request-delete-modal__message">{message}</div>
      </div>
      <div className="budget-change-request-delete-modal__footer">
        <button
          type="button"
          className="budget-change-request-delete-modal__btn budget-change-request-delete-modal__btn--ghost"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="budget-change-request-delete-modal__btn budget-change-request-delete-modal__btn--danger"
          onClick={onConfirm}
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

const InlineTable = ({
  headers,
  rows,
  renderRow,
  totalRow,
  emptyRowCount = 0,
  renderEmptyRow,
  isScrollable = false,
}) => (
  <div className={`bcr-inline-table-wrap${isScrollable ? ' bcr-inline-table-wrap--scrollable' : ''}`}>
    <table className="bcr-inline-table">
      <thead>
        <tr>
          {headers.map((header) => (
            <th
              key={header.label || header}
              className={typeof header === 'object' ? header.className : ''}
            >
              {typeof header === 'object' ? header.label : header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => renderRow(row, index))}
        {Array.from({ length: emptyRowCount }).map((_, index) => renderEmptyRow(index))}
      </tbody>
      {totalRow && (
        <tfoot>
          <tr className="bcr-total-row">{totalRow}</tr>
        </tfoot>
      )}
    </table>
  </div>
);

const TruncatedText = ({ value, className = '' }) => {
  const displayValue = value || '-';

  return (
    <span className={`bcr-truncate-wrap${className ? ` ${className}` : ''}`}>
      <span className="bcr-truncate">
        {displayValue}
      </span>
      {displayValue !== '-' && (
        <span className="bcr-truncate-tooltip" role="tooltip">
          {displayValue}
        </span>
      )}
    </span>
  );
};

const EffortsTab = ({
  effortRows,
  setEffortRows,
  isViewOnly,
  canEditEffort,
  resourceSkillOptions,
  showRequiredErrors,
  onEditingStateChange,
}) => {
  const { snackbarOpen } = useSnackbar();
  const [form, setForm] = useState(EMPTY_EFFORT);
  const [editingIndex, setEditingIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const selectedResourceIds = useMemo(
    () => getSelectedOptionIds(effortRows, editingIndex, 'resourceId'),
    [editingIndex, effortRows],
  );

  const handleFormChange = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (errors[field]) {
      setErrors((current) => ({ ...current, [field]: '' }));
    }
  }, [errors]);

  const validate = useCallback(() => {
    const nextErrors = {};
    if (!form.resourceId) nextErrors.resourceId = 'Resource skill is required';
    if (form.resourceId && selectedResourceIds.has(String(form.resourceId))) {
      nextErrors.resourceId = 'Resource skill already added';
    }
    if (!form.effort || Number.isNaN(parseFloat(form.effort))) {
      nextErrors.effort = 'Effort is required';
    } else if (parseFloat(form.effort) <= 0) {
      nextErrors.effort = 'Effort must be greater than zero';
    }
    return nextErrors;
  }, [form.effort, form.resourceId, selectedResourceIds]);

  const resetForm = useCallback(() => {
    setForm(EMPTY_EFFORT);
    setEditingIndex(null);
    setErrors({});
  }, []);

  useEffect(() => {
    onEditingStateChange?.(editingIndex !== null);
  }, [editingIndex, onEditingStateChange]);

  const handleAdd = useCallback(() => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      if (nextErrors.effort === 'Effort must be greater than zero') {
        snackbarOpen('Effort cannot be zero or below zero.', 'error');
      }
      return;
    }

    const selectedOption = resourceSkillOptions.find((option) => String(option.value) === String(form.resourceId));
    const nextRow = {
      resourceId: String(form.resourceId),
      resourceSkill: selectedOption?.label || form.resourceSkill || '',
      effort: String(form.effort),
      comment: String(form.comment || ''),
    };

    if (editingIndex !== null) {
      setEffortRows((current) => current.map((row, index) => (index === editingIndex ? nextRow : row)));
    } else {
      setEffortRows((current) => [...current, nextRow]);
    }

    resetForm();
  }, [editingIndex, form.comment, form.effort, form.resourceId, form.resourceSkill, resetForm, resourceSkillOptions, setEffortRows, snackbarOpen, validate]);

  const handleEdit = useCallback((index) => {
    setForm({ ...effortRows[index] });
    setEditingIndex(index);
    setErrors({});
  }, [effortRows]);

  const handleDelete = useCallback((index) => {
    setDeleteConfirmIndex(index);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmIndex === null) return;
    setEffortRows((current) => current.filter((_, rowIndex) => rowIndex !== deleteConfirmIndex));
    if (editingIndex === deleteConfirmIndex) {
      resetForm();
    }
    setDeleteConfirmIndex(null);
  }, [deleteConfirmIndex, editingIndex, resetForm, setEffortRows]);

  const total = calcEffortTotal(effortRows);
  const emptyRowCount = Math.max(VISIBLE_REQUEST_ROW_COUNT - effortRows.length, 0);

  if (isViewOnly) {
    return (
      <InlineTable
        headers={[
          'Resource Skills',
          { label: 'Efforts (Hrs)', className: 'bcr-column-header--numeric' },
          'Comments',
        ]}
        rows={effortRows}
        renderRow={(row, index) => (
          <tr key={`${row.resourceId || row.resourceSkill}-${index}`}>
            <td><TruncatedText value={row.resourceSkill} /></td>
            <td className="bcr-cell--numeric">{formatEffort(row.effort)}</td>
            <td><TruncatedText value={row.comment} /></td>
          </tr>
        )}
        emptyRowCount={emptyRowCount}
        renderEmptyRow={(index) => (
          <tr key={`effort-empty-${index}`} className="bcr-empty-row">
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
          </tr>
        )}
        isScrollable={effortRows.length > VISIBLE_REQUEST_ROW_COUNT}
        totalRow={(
          <>
            <td className="bcr-total-label-cell">Total:</td>
            <td className="bcr-total-summary bcr-total-value-cell">
              <span className="bcr-total-value">{formatEffort(total)}</span>
            </td>
            <td />
          </>
        )}
      />
    );
  }

  return (
    <div className="bcr-tab-content">
      {canEditEffort && (
        <>
          <div className="bcr-form-row">
            <FormField
              label="Resource Skills"
              required
              hasError={Boolean(errors.resourceId) || showRequiredErrors}
            >
              <SearchableSelect
                value={form.resourceId}
                selectedLabel={form.resourceId
                  ? (resourceSkillOptions.find((option) => String(option.value) === String(form.resourceId))?.label || form.resourceSkill)
                  : form.resourceSkill}
                placeholder="Select Skills"
                options={resourceSkillOptions}
                disabledOptionIds={selectedResourceIds}
                onInputChange={(nextValue) => {
                  setForm((current) => ({
                    ...current,
                    resourceId: '',
                    resourceSkill: nextValue,
                  }));
                  if (errors.resourceId) {
                    setErrors((current) => ({ ...current, resourceId: '' }));
                  }
                }}
                onSelect={(selectedOption) => {
                  setForm((current) => ({
                    ...current,
                    resourceId: String(selectedOption.value),
                    resourceSkill: selectedOption.label || '',
                  }));
                  if (errors.resourceId) {
                    setErrors((current) => ({ ...current, resourceId: '' }));
                  }
                }}
                onClear={() => {
                  setForm((current) => ({
                    ...current,
                    resourceId: '',
                    resourceSkill: '',
                  }));
                  if (errors.resourceId) {
                    setErrors((current) => ({ ...current, resourceId: '' }));
                  }
                }}
              />
            </FormField>

            <FormField
              label="Effort (Hrs)"
              required
              hasError={Boolean(errors.effort) || showRequiredErrors}
            >
              <input
                type="text"
                inputMode="decimal"
                className="bcr-input"
                placeholder="Enter Effort"
                value={form.effort}
                onChange={(event) => handleFormChange('effort', sanitizeDecimalInput(event.target.value))}
              />
            </FormField>
          </div>

          <FormField label="Comments">
            <input
              type="text"
              className="bcr-input"
              placeholder="Enter Details"
              value={form.comment}
              onChange={(event) => handleFormChange('comment', event.target.value)}
            />
          </FormField>

          <div className="bcr-form-actions">
            <button type="button" className="bcr-btn bcr-btn--ghost" onClick={resetForm}>
              Cancel
            </button>
            <button type="button" className="bcr-btn bcr-btn--primary" onClick={handleAdd}>
              {editingIndex !== null ? 'Update' : 'Add'}
            </button>
          </div>
        </>
      )}

      <InlineTable
        headers={[
          'Resource Skills',
          { label: 'Efforts (Hrs)', className: 'bcr-column-header--numeric' },
          'Comments',
          ...(canEditEffort ? [{ label: 'Action', className: 'bcr-action-header' }] : []),
        ]}
        rows={effortRows}
        renderRow={(row, index) => (
          <tr key={`${row.resourceId || row.resourceSkill}-${index}`} className={editingIndex === index ? 'bcr-row--editing' : ''}>
            <td><TruncatedText value={row.resourceSkill} /></td>
            <td className="bcr-cell--numeric">{formatEffort(row.effort)}</td>
            <td><TruncatedText value={row.comment} /></td>
            {canEditEffort && (
              <td className="bcr-action-cell">
                {editingIndex === index ? (
                  <span className="bcr-editing-tag">Editing...</span>
                ) : (
                  <RowActionButtons
                    onEdit={() => handleEdit(index)}
                    onDelete={() => handleDelete(index)}
                    editLabel="Edit effort row"
                    deleteLabel="Delete effort row"
                  />
                )}
              </td>
            )}
          </tr>
        )}
        emptyRowCount={emptyRowCount}
        renderEmptyRow={(index) => (
          <tr key={`effort-empty-${index}`} className="bcr-empty-row">
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            {canEditEffort && <td className="bcr-action-cell">&nbsp;</td>}
          </tr>
        )}
        totalRow={(
          <>
            <td className="bcr-total-label-cell">Total:</td>
            <td className="bcr-total-combined bcr-total-value-cell">
              <span className="bcr-total-value">{formatEffort(total)}</span>
            </td>
            <td />
            {canEditEffort && <td className="bcr-action-cell" />}
          </>
        )}
      />
      {deleteConfirmIndex !== null && (
        <DeleteConfirmModal
          title="Delete"
          message="Are you sure you want to delete"
          onClose={() => setDeleteConfirmIndex(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

const CostTab = ({
  costRows,
  setCostRows,
  isViewOnly,
  canEditCost,
  costCategoryOptions,
  showRequiredErrors,
  onEditingStateChange,
}) => {
  const { snackbarOpen } = useSnackbar();
  const [form, setForm] = useState(EMPTY_COST);
  const [editingIndex, setEditingIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const selectedCostCategoryIds = useMemo(
    () => getSelectedOptionIds(costRows, editingIndex, 'costCategoryId'),
    [costRows, editingIndex],
  );

  const handleFormChange = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (errors[field]) {
      setErrors((current) => ({ ...current, [field]: '' }));
    }
  }, [errors]);

  const validate = useCallback(() => {
    const nextErrors = {};
    if (!form.costCategoryId) nextErrors.costCategoryId = 'Cost category is required';
    if (form.costCategoryId && selectedCostCategoryIds.has(String(form.costCategoryId))) {
      nextErrors.costCategoryId = 'Cost category already added';
    }
    if (!form.amount || Number.isNaN(parseFloat(String(form.amount).replace(/,/g, '')))) {
      nextErrors.amount = 'Amount is required';
    } else if (parseFloat(String(form.amount).replace(/,/g, '')) <= 0) {
      nextErrors.amount = 'Amount must be greater than zero';
    }
    return nextErrors;
  }, [form.amount, form.costCategoryId, selectedCostCategoryIds]);

  const resetForm = useCallback(() => {
    setForm(EMPTY_COST);
    setEditingIndex(null);
    setErrors({});
  }, []);

  useEffect(() => {
    onEditingStateChange?.(editingIndex !== null);
  }, [editingIndex, onEditingStateChange]);

  const handleAdd = useCallback(() => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      if (nextErrors.amount === 'Amount must be greater than zero') {
        snackbarOpen('Amount cannot be zero or below zero.', 'error');
      }
      return;
    }

    const selectedOption = costCategoryOptions.find((option) => String(option.value) === String(form.costCategoryId));
    const nextRow = {
      costCategoryId: String(form.costCategoryId),
      costCategory: selectedOption?.label || form.costCategory || '',
      amount: String(form.amount),
      comment: String(form.comment || ''),
    };

    if (editingIndex !== null) {
      setCostRows((current) => current.map((row, index) => (index === editingIndex ? nextRow : row)));
    } else {
      setCostRows((current) => [...current, nextRow]);
    }

    resetForm();
  }, [costCategoryOptions, editingIndex, form.amount, form.comment, form.costCategory, form.costCategoryId, resetForm, setCostRows, snackbarOpen, validate]);

  const handleEdit = useCallback((index) => {
    setForm({ ...costRows[index] });
    setEditingIndex(index);
    setErrors({});
  }, [costRows]);

  const handleDelete = useCallback((index) => {
    setDeleteConfirmIndex(index);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmIndex === null) return;
    setCostRows((current) => current.filter((_, rowIndex) => rowIndex !== deleteConfirmIndex));
    if (editingIndex === deleteConfirmIndex) {
      resetForm();
    }
    setDeleteConfirmIndex(null);
  }, [deleteConfirmIndex, editingIndex, resetForm, setCostRows]);

  const total = calcCostTotal(costRows);
  const emptyRowCount = Math.max(VISIBLE_REQUEST_ROW_COUNT - costRows.length, 0);

  if (isViewOnly) {
    return (
      <InlineTable
        headers={[
          'Cost Category',
          { label: 'Amount (INR)', className: 'bcr-column-header--numeric' },
          'Comments',
        ]}
        rows={costRows}
        renderRow={(row, index) => (
          <tr key={`${row.costCategoryId || row.costCategory}-${index}`}>
            <td><TruncatedText value={row.costCategory} /></td>
            <td className="bcr-cell--numeric">{formatAmount(row.amount)}</td>
            <td><TruncatedText value={row.comment} /></td>
          </tr>
        )}
        emptyRowCount={emptyRowCount}
        renderEmptyRow={(index) => (
          <tr key={`cost-empty-${index}`} className="bcr-empty-row">
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
          </tr>
        )}
        isScrollable={costRows.length > VISIBLE_REQUEST_ROW_COUNT}
        totalRow={(
          <>
            <td className="bcr-total-label-cell">Total:</td>
            <td className="bcr-total-summary bcr-total-value-cell">
              <span className="bcr-total-value">{formatAmount(total)}</span>
            </td>
            <td />
          </>
        )}
      />
    );
  }

  return (
    <div className="bcr-tab-content">
      {canEditCost && (
        <>
          <div className="bcr-form-row">
            <FormField
              label="Cost Category"
              required
              hasError={Boolean(errors.costCategoryId) || showRequiredErrors}
            >
              <SearchableSelect
                value={form.costCategoryId}
                selectedLabel={form.costCategoryId
                  ? (costCategoryOptions.find((option) => String(option.value) === String(form.costCategoryId))?.label || form.costCategory)
                  : form.costCategory}
                placeholder="Select Cost Category"
                options={costCategoryOptions}
                disabledOptionIds={selectedCostCategoryIds}
                onInputChange={(nextValue) => {
                  setForm((current) => ({
                    ...current,
                    costCategoryId: '',
                    costCategory: nextValue,
                  }));
                  if (errors.costCategoryId) {
                    setErrors((current) => ({ ...current, costCategoryId: '' }));
                  }
                }}
                onSelect={(selectedOption) => {
                  setForm((current) => ({
                    ...current,
                    costCategoryId: String(selectedOption.value),
                    costCategory: selectedOption.label || '',
                  }));
                  if (errors.costCategoryId) {
                    setErrors((current) => ({ ...current, costCategoryId: '' }));
                  }
                }}
                onClear={() => {
                  setForm((current) => ({
                    ...current,
                    costCategoryId: '',
                    costCategory: '',
                  }));
                  if (errors.costCategoryId) {
                    setErrors((current) => ({ ...current, costCategoryId: '' }));
                  }
                }}
              />
            </FormField>

            <FormField
              label="Amount (INR)"
              required
              hasError={Boolean(errors.amount) || showRequiredErrors}
            >
              <input
                type="text"
                inputMode="decimal"
                className="bcr-input"
                placeholder="Enter Amount"
                value={form.amount}
                onChange={(event) => handleFormChange('amount', sanitizeDecimalInput(event.target.value))}
              />
            </FormField>
          </div>

          <FormField label="Comments">
            <input
              type="text"
              className="bcr-input"
              placeholder="Enter Details"
              value={form.comment}
              onChange={(event) => handleFormChange('comment', event.target.value)}
            />
          </FormField>

          <div className="bcr-form-actions">
            <button type="button" className="bcr-btn bcr-btn--ghost" onClick={resetForm}>
              Cancel
            </button>
            <button type="button" className="bcr-btn bcr-btn--primary" onClick={handleAdd}>
              {editingIndex !== null ? 'Update' : 'Add'}
            </button>
          </div>
        </>
      )}

      <InlineTable
        headers={[
          'Cost Category',
          { label: 'Amount (INR)', className: 'bcr-column-header--numeric' },
          'Comments',
          ...(canEditCost ? [{ label: 'Action', className: 'bcr-action-header' }] : []),
        ]}
        rows={costRows}
        renderRow={(row, index) => (
          <tr key={`${row.costCategoryId || row.costCategory}-${index}`} className={editingIndex === index ? 'bcr-row--editing' : ''}>
            <td><TruncatedText value={row.costCategory} /></td>
            <td className="bcr-cell--numeric">{formatAmount(row.amount)}</td>
            <td><TruncatedText value={row.comment} /></td>
            {canEditCost && (
              <td className="bcr-action-cell">
                {editingIndex === index ? (
                  <span className="bcr-editing-tag">Editing...</span>
                ) : (
                  <RowActionButtons
                    onEdit={() => handleEdit(index)}
                    onDelete={() => handleDelete(index)}
                    editLabel="Edit cost row"
                    deleteLabel="Delete cost row"
                  />
                )}
              </td>
            )}
          </tr>
        )}
        emptyRowCount={emptyRowCount}
        renderEmptyRow={(index) => (
          <tr key={`cost-empty-${index}`} className="bcr-empty-row">
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            {canEditCost && <td className="bcr-action-cell">&nbsp;</td>}
          </tr>
        )}
        totalRow={(
          <>
            <td className="bcr-total-label-cell">Total:</td>
            <td className="bcr-total-combined bcr-total-value-cell">
              <span className="bcr-total-value">{formatAmount(total)}</span>
            </td>
            <td />
            {canEditCost && <td className="bcr-action-cell" />}
          </>
        )}
      />
      {deleteConfirmIndex !== null && (
        <DeleteConfirmModal
          title="Delete"
          message="Are you sure you want to delete"
          onClose={() => setDeleteConfirmIndex(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

const BudgetChangeRequestModal = ({
  mode = 'add',
  eern = '',
  initialData = null,
  onClose,
  onSubmit,
  onStatusSubmit = null,
  permissions = {
    canViewEffort: true,
    canViewCost: true,
    canEditEffort: true,
    canEditCost: true,
    canApproveRequest: false,
  },
  resourceSkillOptions = [],
  costCategoryOptions = [],
  approvalOptions = [],
  requestStatus = '',
  isSubmitting = false,
  isStatusSubmitting = false,
}) => {
  const { snackbarOpen } = useSnackbar();
  const isViewOnly = mode === 'view';
  const canApproveFromView = isViewOnly
    && permissions.canApproveRequest
    && String(requestStatus || '').trim()
      .toLowerCase() === OPEN_REQUEST_STATUS;

  const tabs = useMemo(() => {
    const nextTabs = [];

    if (isViewOnly) {
      if (permissions.canViewEffort) nextTabs.push('efforts');
      if (permissions.canViewCost) nextTabs.push('cost');
      if (!nextTabs.length) nextTabs.push('efforts');
      return nextTabs;
    }

    if (permissions.canEditEffort) nextTabs.push('efforts');
    if (permissions.canEditCost) nextTabs.push('cost');
    if (!nextTabs.length) nextTabs.push('efforts');

    return nextTabs;
  }, [
    isViewOnly,
    permissions.canEditCost,
    permissions.canEditEffort,
    permissions.canViewCost,
    permissions.canViewEffort,
  ]);

  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [effortRows, setEffortRows] = useState(() => normalizeEffortRows(initialData?.effortRows));
  const [costRows, setCostRows] = useState(() => normalizeCostRows(initialData?.costRows));
  const [reasonForVersionChange, setReasonForVersionChange] = useState(
    initialData?.reasonForVersionChange || '',
  );
  const [reasonError, setReasonError] = useState('');
  const [requestRowsError, setRequestRowsError] = useState(false);
  const [hasUnsavedEffortEdit, setHasUnsavedEffortEdit] = useState(false);
  const [hasUnsavedCostEdit, setHasUnsavedCostEdit] = useState(false);
  const [selectedApprovalAction, setSelectedApprovalAction] = useState('');
  const [approvalComment, setApprovalComment] = useState('');

  useEffect(() => {
    setActiveTab(tabs[0]);
  }, [tabs]);

  useEffect(() => {
    setEffortRows(normalizeEffortRows(initialData?.effortRows));
    setCostRows(normalizeCostRows(initialData?.costRows));
    setReasonForVersionChange(initialData?.reasonForVersionChange || '');
    setReasonError('');
    setRequestRowsError(false);
    setHasUnsavedEffortEdit(false);
    setHasUnsavedCostEdit(false);
    setSelectedApprovalAction('');
    setApprovalComment('');
  }, [initialData]);

  const handleReasonChange = useCallback((value) => {
    setReasonForVersionChange(value);
    if (value.trim()) setReasonError('');
  }, []);

  useEffect(() => {
    if (effortRows.length > 0 || costRows.length > 0) {
      setRequestRowsError(false);
    }
  }, [costRows.length, effortRows.length]);

  const handleSubmit = useCallback((isDraft = false) => {
    if (hasUnsavedEffortEdit || hasUnsavedCostEdit) {
      snackbarOpen(
        isDraft
          ? 'Changes are not saved. Please click Add or Update before saving as draft.'
          : 'Changes are not saved, hence cannot submit.',
        'error',
      );
      return;
    }

    if (!reasonForVersionChange.trim()) {
      setReasonError('Reason for Version Change is required');
      return;
    }

    if (effortRows.length === 0 && costRows.length === 0) {
      setRequestRowsError(true);
      snackbarOpen('Add at least one effort or cost row before saving.', 'error');
      return;
    }

    onSubmit?.({
      effortRows,
      costRows,
      reasonForVersionChange: reasonForVersionChange.trim(),
    }, isDraft);
  }, [
    costRows,
    effortRows,
    hasUnsavedCostEdit,
    hasUnsavedEffortEdit,
    onSubmit,
    reasonForVersionChange,
    snackbarOpen,
  ]);

  const canSubmitApproval = Boolean(selectedApprovalAction && approvalComment.trim());

  const handleStatusSubmit = useCallback(() => {
    if (!canSubmitApproval) return;
    const selectedOption = approvalOptions.find((option) => option.value === selectedApprovalAction) || null;
    if (!selectedOption) return;
    onStatusSubmit?.({
      option: selectedOption,
      comment: approvalComment.trim(),
    });
  }, [approvalComment, approvalOptions, canSubmitApproval, onStatusSubmit, selectedApprovalAction]);

  const title = isViewOnly || mode === 'edit' ? `Request - ${eern}` : 'Request - New';

  return (
    <div className="bcr-modal-backdrop" onClick={(event) => event.target === event.currentTarget && onClose?.()}>
      <div className={`bcr-modal${isViewOnly ? ' bcr-modal--view' : ''}`}>
        <div className="bcr-modal-header">
          <h3 className="bcr-modal-title">{title}</h3>
        </div>

        <div className={`bcr-tabs${isViewOnly ? ' bcr-tabs--view' : ''}`}>
          {tabs.includes('efforts') && (
            <button
              type="button"
              className={`bcr-tab${activeTab === 'efforts' ? ' bcr-tab--active' : ''}`}
              aria-pressed={activeTab === 'efforts'}
              onClick={() => setActiveTab('efforts')}
            >
              Efforts
            </button>
          )}
          {tabs.includes('cost') && (
            <button
              type="button"
              className={`bcr-tab${activeTab === 'cost' ? ' bcr-tab--active' : ''}`}
              aria-pressed={activeTab === 'cost'}
              onClick={() => setActiveTab('cost')}
            >
              Cost
            </button>
          )}
        </div>

        <div className={`bcr-modal-body${isViewOnly ? ' bcr-modal-body--view' : ''}`}>
          {activeTab === 'efforts' && (
            <EffortsTab
              effortRows={effortRows}
              setEffortRows={setEffortRows}
              isViewOnly={isViewOnly}
              canEditEffort={permissions.canEditEffort}
              resourceSkillOptions={resourceSkillOptions}
              showRequiredErrors={requestRowsError}
              onEditingStateChange={setHasUnsavedEffortEdit}
            />
          )}

          {activeTab === 'cost' && (
            <CostTab
              costRows={costRows}
              setCostRows={setCostRows}
              isViewOnly={isViewOnly}
              canEditCost={permissions.canEditCost}
              costCategoryOptions={costCategoryOptions}
              showRequiredErrors={requestRowsError}
              onEditingStateChange={setHasUnsavedCostEdit}
            />
          )}

          {!isViewOnly && (
            <div className="bcr-reason-wrap">
              <label className={`bcr-form-label${reasonError ? ' bcr-form-label--error' : ''}`}>
                Reason for Version Change
                <span className="bcr-required">*</span>
              </label>
              <textarea
                className="bcr-textarea"
                placeholder="Enter Details"
                value={reasonForVersionChange}
                onChange={(event) => handleReasonChange(event.target.value)}
                rows={3}
              />
            </div>
          )}

          {isViewOnly && reasonForVersionChange && (
            <div className="bcr-reason-wrap bcr-reason-wrap--view">
              <label className="bcr-form-label">Reason for Version Change</label>
              <div className="bcr-reason-view">{reasonForVersionChange}</div>
            </div>
          )}

          {canApproveFromView && (
            <div className="bcr-approval-wrap">
              <div className="bcr-approval-field">
                <label className="bcr-approval-label">
                  Status
                  <span className="bcr-required">*</span>
                </label>
                <select
                  className={`bcr-select bcr-approval-select${selectedApprovalAction ? ' has-value' : ''}`}
                  value={selectedApprovalAction}
                  onChange={(event) => {
                    setSelectedApprovalAction(event.target.value);
                  }}
                  disabled={isStatusSubmitting}
                >
                  <option value="" disabled>Select</option>
                  {approvalOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bcr-approval-field">
                <label className="bcr-approval-label">
                  Comment
                  <span className="bcr-required">*</span>
                </label>
                <textarea
                  className="bcr-textarea bcr-approval-textarea"
                  placeholder="Enter..."
                  value={approvalComment}
                  onChange={(event) => {
                    setApprovalComment(event.target.value);
                  }}
                  rows={4}
                  disabled={isStatusSubmitting}
                />
              </div>
            </div>
          )}
        </div>

        {!isViewOnly && (
          <div className="bcr-modal-footer">
            <button type="button" className="bcr-btn bcr-btn--ghost" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="button"
              className="bcr-btn bcr-btn--outline"
              onClick={() => handleSubmit(true)}
              disabled={isSubmitting}
            >
              Save as Draft
            </button>
            <button
              type="button"
              className="bcr-btn bcr-btn--primary"
              onClick={() => handleSubmit(false)}
              disabled={isSubmitting}
            >
              Submit
            </button>
          </div>
        )}

        {isViewOnly && (
          <div className="bcr-modal-footer bcr-modal-footer--view">
            {canApproveFromView ? (
              <>
                <button
                  type="button"
                  className="bcr-btn bcr-btn--ghost"
                  onClick={onClose}
                  disabled={isStatusSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bcr-btn bcr-btn--primary"
                  onClick={handleStatusSubmit}
                  disabled={isStatusSubmitting || !canSubmitApproval}
                >
                  Save
                </button>
              </>
            ) : (
              <button type="button" className="bcr-btn bcr-btn--primary bcr-btn--view-close" onClick={onClose}>
                Close
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

FormField.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  showRequiredMark: PropTypes.bool,
  hasError: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

FormField.defaultProps = {
  required: false,
  showRequiredMark: true,
  hasError: false,
};

RowActionButtons.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  editLabel: PropTypes.string.isRequired,
  deleteLabel: PropTypes.string.isRequired,
};

EffortsTab.propTypes = {
  effortRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  setEffortRows: PropTypes.func.isRequired,
  isViewOnly: PropTypes.bool.isRequired,
  canEditEffort: PropTypes.bool.isRequired,
  resourceSkillOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  })),
  showRequiredErrors: PropTypes.bool.isRequired,
  onEditingStateChange: PropTypes.func,
};

EffortsTab.defaultProps = {
  resourceSkillOptions: [],
  onEditingStateChange: null,
};

CostTab.propTypes = {
  costRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  setCostRows: PropTypes.func.isRequired,
  isViewOnly: PropTypes.bool.isRequired,
  canEditCost: PropTypes.bool.isRequired,
  costCategoryOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  })),
  showRequiredErrors: PropTypes.bool.isRequired,
  onEditingStateChange: PropTypes.func,
};

CostTab.defaultProps = {
  costCategoryOptions: [],
  onEditingStateChange: null,
};

InlineTable.propTypes = {
  headers: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  renderRow: PropTypes.func.isRequired,
  totalRow: PropTypes.node,
  emptyRowCount: PropTypes.number,
  renderEmptyRow: PropTypes.func,
  isScrollable: PropTypes.bool,
};

InlineTable.defaultProps = {
  totalRow: null,
  emptyRowCount: 0,
  renderEmptyRow: () => null,
  isScrollable: false,
};

TruncatedText.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
};

TruncatedText.defaultProps = {
  value: '',
  className: '',
};

BudgetChangeRequestModal.propTypes = {
  mode: PropTypes.string,
  eern: PropTypes.string,
  initialData: PropTypes.shape({
    effortRows: PropTypes.arrayOf(PropTypes.object),
    costRows: PropTypes.arrayOf(PropTypes.object),
    reasonForVersionChange: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onStatusSubmit: PropTypes.func,
  permissions: PropTypes.shape({
    canViewEffort: PropTypes.bool,
    canViewCost: PropTypes.bool,
    canEditEffort: PropTypes.bool,
    canEditCost: PropTypes.bool,
    canApproveRequest: PropTypes.bool,
  }),
  resourceSkillOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  })),
  costCategoryOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  })),
  approvalOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })),
  requestStatus: PropTypes.string,
  isSubmitting: PropTypes.bool,
  isStatusSubmitting: PropTypes.bool,
};

BudgetChangeRequestModal.defaultProps = {
  mode: 'add',
  eern: '',
  initialData: null,
  onStatusSubmit: null,
  permissions: {
    canViewEffort: true,
    canViewCost: true,
    canEditEffort: true,
    canEditCost: true,
    canApproveRequest: false,
  },
  resourceSkillOptions: [],
  costCategoryOptions: [],
  approvalOptions: [],
  requestStatus: '',
  isSubmitting: false,
  isStatusSubmitting: false,
};

export default BudgetChangeRequestModal;
